import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import {
  TtqWorkbenchSourceParticle,
  TtqWorkbenchSourceParticleGroup,
} from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell, useTtqWorkbenchContext } from '../../features/ttqib/ttqWorkbenchShared';
import { knowledgeService } from '../../services/knowledgeService';
import type { KnowledgeParticle } from '../../types/domain';

function readQuestionText(questions: unknown): string {
  if (typeof questions === 'string') return questions;
  if (questions && typeof questions === 'object' && 'main' in questions) {
    return String((questions as { main?: string }).main || '');
  }
  return '';
}

function scoreSimilarity(source: string, candidate: string): number {
  const a = source.toLowerCase();
  const b = candidate.toLowerCase();
  if (!a || !b) return 0;
  const words = [...new Set(a.split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 1))];
  let hit = 0;
  for (const w of words) if (b.includes(w)) hit += 1;
  return hit / Math.max(words.length, 1);
}

export function TtqPatternUnifyWorkbenchPage() {
  const { ttqaId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const particleIds = useMemo(
    () => (searchParams.get('particles') || '').split(',').map((s) => s.trim()).filter(Boolean),
    [searchParams],
  );
  const primaryId = particleIds[0] || '';
  const ctx = useTtqWorkbenchContext(ttqaId, primaryId);
  const [candidates, setCandidates] = useState<Array<KnowledgeParticle & { score: number }>>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const split = !ctx.loading;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  useEffect(() => {
    if (!user?.id || !ctx.questionText || particleIds.length === 0) return;
    let active = true;
    setSearching(true);
    const query = ctx.questionText.slice(0, 120);
    void knowledgeService.listKnowledgeParticles({
      userId: user.id,
      page: 1,
      pageSize: 40,
      search: query,
      subject: ctx.session?.subjectCode,
    })
      .then((res) => {
        if (!active) return;
        const sourceSet = new Set(particleIds);
        const ranked = res.data
          .filter((p) => !sourceSet.has(p.id))
          .map((p) => ({
            ...p,
            score: scoreSimilarity(ctx.questionText, readQuestionText(p.questions) + (p.title || '')),
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 20);
        setCandidates(ranked);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : '检索失败'))
      .finally(() => active && setSearching(false));
    return () => { active = false; };
  }, [user?.id, ctx.questionText, ctx.session?.subjectCode, particleIds]);

  const sourcePanel = particleIds.length > 1 ? (
    <TtqWorkbenchSourceParticleGroup
      items={particleIds.map((id) => ({ particleId: id }))}
    />
  ) : primaryId ? (
    <TtqWorkbenchSourceParticle
      particleId={primaryId}
      particle={ctx.particle}
      sourceImage={ctx.sourceImage}
      imageLoading={ctx.imageLoading}
      particleLoading={ctx.loading}
    />
  ) : null;

  return (
    <TtqWorkbenchShell
      ttqaId={ttqaId}
      title="一解多题 · 相似题检索"
      angleId="pattern_unify"
      loading={ctx.loading}
      error={ctx.error || error}
      restoreParticleIds={particleIds}
      toolbar={(
        <TtqWorkbenchToolbar
          showFocusToggle={split}
          leftLabel="源题"
          rightLabel="相似题"
          emphasis={emphasis}
          onEmphasisChange={setEmphasis}
        />
      )}
    >
      <p className="text-xs text-muted">
        基于源题求解思路，检索库中题型/解法相近的知识颗粒（{particleIds.length} 颗源题）
      </p>
      <TtqWorkbenchFocusLayout
        split={split}
        emphasis={emphasis}
        source={sourcePanel}
        derived={(
          <section className="rounded-xl border border-border bg-surface p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink">
              <Search size={14} />
              求解思路相似颗粒
            </div>
            {searching ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-primary" /></div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-muted">未找到相似颗粒</p>
            ) : (
              <ul className="max-h-[calc(100vh-12rem)] space-y-2 overflow-y-auto pr-1">
                {candidates.map((p) => (
                  <li key={p.id} className="rounded-lg border border-border p-3 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-ink">{p.title || '未命名颗粒'}</p>
                      <span className="shrink-0 text-[10px] text-muted">{Math.round(p.score * 100)}%</span>
                    </div>
                    <p className="mt-1 line-clamp-3 text-xs text-muted whitespace-pre-wrap">
                      {readQuestionText(p.questions)}
                    </p>
                    <Link to={`/knowledge/${p.id}`} className="mt-2 inline-block text-xs text-primary hover:underline">
                      打开颗粒
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      />
    </TtqWorkbenchShell>
  );
}
